import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { DiagnosticRTCServiceType, useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import DiagnosticMedicalVideo from '../components/DiagnosticMedicalVideo'
import type { DiagnosticMedicalVideoProps } from '../components/DiagnosticMedicalVideo'
import type { MedicalVideoHandle } from '@/components/group/MedicalVideo'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectBackgroundTasks, startBackgroundRequestRpiId, stopBackgroundRequestRpiId } from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { joinMeeting, leaveMeeting, selectConsultationById, selectRpiMeetingId } from '@/redux/reducers/consultation.reducer'
import { addDocument, selectDocumentCreateRequestTracker } from '@/redux/reducers/document.reducer'
import { selectRoomByConsultationId, updateRoom } from '@/redux/reducers/room.reducer'
import { useLazyGetMagicLinkQuery } from '@/core/services/auth'
import { useElaraActions } from '@/providers/elara/hooks/useElaraActions'
import { useElaraState } from '@/providers/elara/hooks/useElaraState'
import type { UIAction } from '@/providers/elara/types'

export type DiagnosticMedicalVideoContainerProps = Pick<
  DiagnosticMedicalVideoProps,
  'disableStreamOnMobile' | 'videoHeight' | 'videoWidth' | 'fitVideoToStream' | 'maxVideoHeight'
> & {
  consultationId?: string
  /** For group consultations: the consultation ID linked to the shared room */
  roomConsultationId?: string
}

export function DiagnosticMedicalVideoContainer(props: DiagnosticMedicalVideoContainerProps) {
  const { consultationId, roomConsultationId, disableStreamOnMobile, ...rest } = props
  const effectiveRoomConsultationId = roomConsultationId || consultationId
  const appDispatch = useAppDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { activeDiagnosticRTCService, isStreamer, captureScreen, selectDevice, devices } = useDiagnosticRTC()

  const hasSwitchedStreamingMedicalVideoOnMobile = useRef(false)
  const medicalVideoRef = useRef<MedicalVideoHandle>(null)

  const documentCreateRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const authUser = useAppSelector(selectAuthUser)
  const backgroundTasks = useAppSelector(selectBackgroundTasks)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const room = useAppSelector((state) => (effectiveRoomConsultationId ? selectRoomByConsultationId(state, effectiveRoomConsultationId) : undefined))
  const rpiMeetingId = useAppSelector(selectRpiMeetingId)
  // const qrCode = useAppSelector(selectMagicLink)

  const [getMagicLink, { isLoading: isGeneratingMagicLink, url }] = useLazyGetMagicLinkQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      url: data?.url,
    }),
  })


  useEffect(() => {
    if (isStreamer || !effectiveRoomConsultationId || !!rpiMeetingId) return

    appDispatch(startBackgroundRequestRpiId(effectiveRoomConsultationId))

    return () => {
      appDispatch(stopBackgroundRequestRpiId())
    }
  }, [appDispatch, effectiveRoomConsultationId, isStreamer, rpiMeetingId])

  useEffect(() => {
    if (backgroundTasks.requestRpiId === true && !isStreamer && rpiMeetingId) {
      appDispatch(stopBackgroundRequestRpiId())
    }
  }, [appDispatch, isStreamer, rpiMeetingId, backgroundTasks])

  useEffect(() => {
    if (activeDiagnosticRTCService === DiagnosticRTCServiceType.Chime && authUser && effectiveRoomConsultationId) {
      appDispatch(joinMeeting({ roomOrConsultationId: effectiveRoomConsultationId, userId: authUser._id, meetingType: 'medicalVideo' }))
    }
  }, [appDispatch, activeDiagnosticRTCService, authUser, effectiveRoomConsultationId])

  useEffect(() => {
    if (disableStreamOnMobile || !room || !isStreamer || !!hasSwitchedStreamingMedicalVideoOnMobile.current) return

    if (room.streamer.endsWith('mobile')) {
      appDispatch(updateRoom(room._id, { streamer: _.replace(room.streamer, /mobile/g, '') }))
    }
  }, [appDispatch, disableStreamOnMobile, isStreamer, room])

  useEffect(() => {
    return () => {
      if (activeDiagnosticRTCService === DiagnosticRTCServiceType.Chime) appDispatch(leaveMeeting('medicalVideo'))
    }
  }, [appDispatch, activeDiagnosticRTCService])

  const handleToggleStreamingOnMobile = (isStreamingOnMobile: boolean) => {
    if (!room) return
    if (!hasSwitchedStreamingMedicalVideoOnMobile.current) hasSwitchedStreamingMedicalVideoOnMobile.current = true
    appDispatch(updateRoom(room._id, { streamer: isStreamingOnMobile ? _.replace(room.streamer, /mobile/g, '') + 'mobile' : _.replace(room.streamer, /mobile/g, '') }))
    if (isStreamingOnMobile) {
      getMagicLink(`${window.location.origin}/m/medical-video?consultationId=${effectiveRoomConsultationId}`)
    }
  }

  const handleUploadDocument = (documentFile: File) => {
    if (consultationId) documentCreateRequestHandler.dispatch(addDocument({ documentFile, objectType: 'consultation', objectId: consultationId }))
  }

  // Resolve the best camera device: prefer identified medical cameras, fallback to first video input
  const resolveCameraDeviceId = useCallback((deviceId?: string): string | undefined => {
    if (deviceId) return deviceId
    if (devices.medicalCameras?.length) return devices.medicalCameras[0].device.deviceId
    if (devices.videoMediaDevices?.length) return devices.videoMediaDevices[0].deviceId
    return undefined
  }, [devices])

  const elaraActions = useMemo((): UIAction[] => [
    {
      actionId: 'medicalVideo.takeScreenshot',
      description: 'Capture a screenshot from the medical video camera',
      section: 'medical-video',
      handler: async () => {
        return new Promise((resolve) => {
          captureScreen((file: File) => {
            handleUploadDocument(file)
            resolve({ success: true, message: 'Screenshot captured' })
          })
        })
      },
    },
    {
      actionId: 'medicalVideo.selectCamera',
      description: 'Select a camera for medical video. If no deviceId is provided, auto-selects the first available medical camera.',
      section: 'medical-video',
      params: [{ name: 'deviceId', type: 'string' as const, description: 'Device ID of the camera (optional — auto-selects if omitted)', required: false }],
      handler: async (params) => {
        const deviceId = resolveCameraDeviceId(params?.deviceId)
        if (!deviceId) return { success: false, message: 'No camera device available' }
        selectDevice(deviceId)
        return { success: true, message: `Camera selected` }
      },
    },
    {
      actionId: 'medicalVideo.startRecording',
      description: 'Start recording video from the medical camera. Optional durationSeconds for timed recording (max 120s).',
      section: 'medical-video',
      params: [{ name: 'durationSeconds', type: 'number' as const, description: 'Auto-stop after N seconds (optional, max 120)', required: false }],
      handler: async (params) => {
        if (!medicalVideoRef.current) return { success: false, message: 'Medical video not ready' }
        // Try to start, retry once after a short wait if stream isn't ready yet
        let started = medicalVideoRef.current.startRecording()
        if (!started) {
          await new Promise((r) => setTimeout(r, 1000))
          started = medicalVideoRef.current.startRecording()
        }
        if (!started) return { success: false, message: 'No video stream available — select a camera first' }
        if (params?.durationSeconds && Number(params.durationSeconds) > 0) {
          const ms = Math.min(Number(params.durationSeconds), 120) * 1000
          setTimeout(() => { medicalVideoRef.current?.stopRecording() }, ms)
        }
        return { success: true, message: `Video recording started${params?.durationSeconds ? ` (${Math.min(Number(params.durationSeconds), 120)}s)` : ''}` }
      },
    },
    {
      actionId: 'medicalVideo.stopRecording',
      description: 'Stop the current medical video recording and save it',
      section: 'medical-video',
      handler: async () => {
        if (!medicalVideoRef.current) return { success: false, message: 'Medical video not ready' }
        medicalVideoRef.current.stopRecording()
        return { success: true, message: 'Video recording stopped' }
      },
    },
  ], [captureScreen, selectDevice, resolveCameraDeviceId])

  useElaraActions(elaraActions)

  // Report medical video state to ELARA so the agent knows current recording status
  const medicalVideoState = useMemo(() => ({
    isRecording: !!medicalVideoRef.current?.isRecording,
    hasCamera: (devices.medicalCameras?.length ?? 0) > 0 || (devices.videoMediaDevices?.length ?? 0) > 0,
  }), [medicalVideoRef.current?.isRecording, devices.medicalCameras, devices.videoMediaDevices])
  useElaraState('medical-video', medicalVideoState)

  if (!consultation) return null

  return (
    <DiagnosticMedicalVideo
      {...rest}
      ref={medicalVideoRef}
      consultation={consultation}
      consultationId={consultationId}
      disableStreamOnMobile={disableStreamOnMobile}
      onRecordStop={handleUploadDocument}
      onScreenshot={handleUploadDocument}
      onSaveFreezedPhoto={handleUploadDocument}
      onSaveFreezedVideo={handleUploadDocument}
      onToggleStreamingOnMobile={handleToggleStreamingOnMobile}
      streamingOnMobileLinkQrCode={url}
      isLoadingQrCodeMobile={isGeneratingMagicLink}
    />
  )
}

export default DiagnosticMedicalVideoContainer
